import { jsx as _jsx } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import { gridPageSizeSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { debounce } from 'essential-utils';
import React, { useEffect, useState } from 'react';
import { FlexBox } from '../../box';
import { ColorIndicator } from '../../color-indicator';
import { useMounted } from '../../hooks';
import { Skeleton } from '../../skeleton';
import { useDataGridDensity, useDataGridDensityFactor } from '..';
const MAX_WIDTHS = ['40%', '50%', '60%'];
export const SkeletonOverlay = (props) => {
    const { palette } = useTheme();
    const mounted = useMounted();
    const apiRef = useGridApiContext();
    const rows = useGridSelector(apiRef, gridPageSizeSelector) ?? 20;
    const density = useDataGridDensity();
    const { densityFactor } = useDataGridDensityFactor(apiRef, density);
    const [isReady, setIsReady] = useState(false);
    const height = props.rowHeight * densityFactor;
    const [columns, setColumns] = useState(() => apiRef.current.getVisibleColumns() ?? []);
    useEffect(() => {
        let animationFrame;
        const updateColumns = debounce(() => {
            animationFrame = requestAnimationFrame(() => {
                if (mounted.current) {
                    setColumns(apiRef.current.getVisibleColumns() ?? []);
                }
            });
        }, 0);
        const disposers = [
            apiRef.current.subscribeEvent('columnsChange', updateColumns),
            apiRef.current.subscribeEvent('columnVisibilityModelChange', updateColumns),
            apiRef.current.subscribeEvent('columnWidthChange', updateColumns),
            apiRef.current.subscribeEvent('resize', updateColumns)
        ];
        updateColumns();
        return () => {
            updateColumns.cancel();
            if (animationFrame !== undefined) {
                cancelAnimationFrame(animationFrame);
            }
            disposers.forEach((dispose) => dispose());
        };
    }, [apiRef, mounted]);
    useEffect(() => {
        // Timers are not disposable in browser environments.
        // eslint-disable-next-line ml-rules/prefer-using-for-disposable
        const timeout = setTimeout(() => {
            setIsReady(true);
        }, 250);
        return () => clearTimeout(timeout);
    }, []);
    const isColor = (column) => {
        return (typeof column.headerClassName === 'string' &&
            column.headerClassName.includes('-columnHeader-color'));
    };
    const isFacility = (column) => {
        return (typeof column.headerClassName === 'string' &&
            /-columnHeader-(async)?FacilityInfo/i.test(column.headerClassName));
    };
    const isContextMenu = (column) => {
        return (typeof column.headerClassName === 'string' &&
            column.headerClassName.includes('-columnHeader-contextMenu'));
    };
    const getSkeletonWidth = (column, y, x) => {
        if (isFacility(column)) {
            return 41;
        }
        if (isContextMenu(column)) {
            return 16;
        }
        return maxWidths[y][x];
    };
    const getSkeletonHAlign = (column) => {
        if (isColor(column)) {
            return 'center';
        }
        if (isContextMenu(column)) {
            return 'flex-end';
        }
        return 'flex-start';
    };
    const maxWidths = React.useMemo(() => Array.from({ length: rows }, () => columns.map(() => getRandomMaxWidth())), [columns, rows]);
    if (props.gridConfig?.loading || !isReady) {
        return null;
    }
    return (_jsx(FlexBox, { stack: true, zIndex: 9, position: "relative", bgcolor: ({ palette }) => palette.background.paper, "data-role": "skeleton-overlay", children: Array.from({ length: rows }).map((_, rowIndex) => (_jsx(FlexBox, { stretch: true, borderBottom: "1px solid", borderColor: ({ palette }) => palette.elevation?.outlined, children: columns.map((column, columnIndex) => (_jsx(FlexBox, { hAlign: getSkeletonHAlign(column), vAlign: "center", width: column.computedWidth, flexShrink: 0, flexGrow: 0, flexBasis: column.computedWidth, height: height, px: "10px", children: isColor(column) ? (_jsx(ColorIndicator, { color: palette.action.hover })) : (_jsx(Skeleton, { variant: "rectangular", width: getSkeletonWidth(column, rowIndex, columnIndex), height: 10, color: palette.action.hover })) }, `${column.field}-${column.computedWidth}`))) }, rowIndex))) }));
};
const getRandomMaxWidth = () => {
    return MAX_WIDTHS[Math.floor(Math.random() * MAX_WIDTHS.length)];
};
